import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { FaHome, FaTimes } from "react-icons/fa";
import type { U2000FavoriteMenuHandlersReturn } from "../../hooks/useU2000FavoriteMenuHandlers";
import type { U2000FavoriteMenuItem } from "../../types/U2000FavoriteMenu";

interface U2000DefaultScreenModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Instance unique du hook favoris (partagée avec le menu — BL-0337) */
  favoriteHandlers: U2000FavoriteMenuHandlersReturn;
}

const U2000DefaultScreenModal: React.FC<U2000DefaultScreenModalProps> = ({ isOpen, onClose, favoriteHandlers }) => {
  const { t } = useTranslation();
  const [selectedPath, setSelectedPath] = useState<string>("");
  const [favoriteItems, setFavoriteItems] = useState<U2000FavoriteMenuItem[]>([]);
  const [currentDefaultScreen, setCurrentDefaultScreen] = useState<string | null>(null);

  // Effet simple qui se déclenche seulement à l'ouverture/fermeture
  useEffect(() => {
    if (isOpen) {
      // Recharger depuis localStorage puis lire via l'instance partagée du menu
      favoriteHandlers.reloadData();
      const items = favoriteHandlers.getFavoriteItems();
      const defaultScreen = favoriteHandlers.getDefaultScreen();

      setFavoriteItems([...items]);
      setCurrentDefaultScreen(defaultScreen);
      setSelectedPath(defaultScreen || "");
    } else {
      // Reset quand fermé
      setSelectedPath("");
      setFavoriteItems([]);
      setCurrentDefaultScreen(null);
    }
    // favoriteHandlers stable via instance parent ; isOpen pilote le chargement
    // eslint-disable-next-line react-hooks/exhaustive-deps -- recharger uniquement à l'ouverture
  }, [isOpen]);

  const handleConfirm = () => {
    if (selectedPath === "") {
      favoriteHandlers.clearDefaultScreen();
    } else {
      favoriteHandlers.setDefaultScreen(selectedPath);
    }

    // Déclencher un événement personnalisé pour notifier les autres composants
    const event = new CustomEvent("defaultScreenChanged", {
      detail: { newDefaultScreen: selectedPath === "" ? null : selectedPath },
    });
    window.dispatchEvent(event);

    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed left-14 top-6 z-50">
      <div className="w-[320px] bg-white rounded-lg shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between bg-gray-800 text-white px-4 py-3">
          <div className="flex items-center space-x-3 min-w-0">
            <div className="bg-blue-600 p-1.5 rounded-lg shrink-0">
              <FaHome className="w-4 h-4 text-white" />
            </div>
            <div className="flex flex-col min-w-0">
              <span className="text-white font-semibold text-sm">{t("Set Default Screen")}</span>
            </div>
          </div>
          <button onClick={handleCancel} className="p-1 rounded-sm hover:bg-gray-700 transition-colors">
            <FaTimes className="h-4 w-4 text-gray-300" />
          </button>
        </div>

        {/* Content */}
        <div className="p-3">
          {favoriteItems.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <FaHome className="h-8 w-8 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">{t("No favorite screens available")}</p>
              <p className="text-xs mt-1">{t("Add some favorites first to set a default screen")}</p>
            </div>
          ) : (
            <div className="max-h-[300px] overflow-auto pr-2">
              <ul className="space-y-1">
                {/* Option pour aucun écran par défaut */}
                <li>
                  <label className="w-full flex items-center gap-3 rounded-sm px-3 py-1.5 hover:bg-gray-100 cursor-pointer">
                    <input
                      type="radio"
                      name="defaultScreen"
                      value=""
                      checked={selectedPath === ""}
                      onChange={() => setSelectedPath("")}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    />
                    <div className="flex-1">
                      <div className="font-medium text-gray-800">{t("No default screen")}</div>
                      <div className="text-xs text-gray-500">{t("Use system default")}</div>
                    </div>
                  </label>
                </li>

                {/* Divider */}
                <li>
                  <div className="border-t border-gray-200 my-1"></div>
                </li>

                {/* Favoris disponibles */}
                {favoriteItems.map((item: U2000FavoriteMenuItem) => (
                  <li key={item.id}>
                    <label className="w-full flex items-center gap-3 rounded-sm px-3 py-1.5 hover:bg-gray-100 cursor-pointer">
                      <input
                        type="radio"
                        name="defaultScreen"
                        value={item.path}
                        checked={selectedPath === item.path}
                        onChange={() => setSelectedPath(item.path)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                      />
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <span className="font-medium text-gray-800">{item.name}</span>
                          {currentDefaultScreen === item.path && <FaHome className="h-3 w-3 text-blue-500 shrink-0" title={t("Current default")} />}
                        </div>
                      </div>
                    </label>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 p-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={handleCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
          >
            {t("Cancel")}
          </button>
          <button
            onClick={handleConfirm}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
          >
            {t("Confirm")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default U2000DefaultScreenModal;
